import { wechatMpSceneConfig } from '@/config';
import { NEW_USER_TEMP_SHARE_DISTRIBUTOR, PAGE_PATH, TOKEN, WECHAT_SCENE_KEY } from '@/constants';
import { sceneConfig } from '@/local-conf';
import { fetchAddCustomerSourceMiniProgram, fetchDesCode } from '@/services';
import { useAppStore, useUserStore } from '@/store';
import { CustomJumpType, OutsideComein } from '@/types';
import type { Common, MpApp } from '@/types/global-business';
import { nextTick } from 'vue';
import { businessUtils } from '.';
import { storage } from '../storage';
import outsideComein from './outside-comein';
// #ifdef H5
import WechatSDK from 'weixin-js-sdk';
// #endif

/**
 * 根据外部场景进入类型执行对应操作
 * @param type - 来源类型
 * @param params - 携带参数
 * @returns
 */
const executeOnShowActions = async (type: OutsideComein, params: any, extra?: any) => {
  await nextTick();

  const actions: Common.RespondAction<OutsideComein> = {
    [OutsideComein.checkinPage]: () => outsideComein.checkinPage(params),
    [OutsideComein.coupon]: () => outsideComein.coupon(params),
    [OutsideComein.couponBundle]: () => outsideComein.couponBundle(params),
    [OutsideComein.customPage]: () => outsideComein.customPage(params),
    [OutsideComein.homePage]: () => outsideComein.homePage(),
    [OutsideComein.commodityGroupPage]: () => outsideComein.commodityGroupPage(),
    [OutsideComein.distribution]: () => outsideComein.distribution(params),
    [OutsideComein.memberShipPlan]: () => outsideComein.memberShipPlan(params),
    [OutsideComein.memberCenter]: () => outsideComein.memberCenterPage(),
    [OutsideComein.product]: () => outsideComein.product(params),
    [OutsideComein.purchasePremium]: () => outsideComein.purchasePremium(params, extra),
    [OutsideComein.discountActivity]: () => outsideComein.discountActivity(params, extra),
    [OutsideComein.packageDiscountActivity]: () => outsideComein.packageDiscount(params, extra),
    [OutsideComein.fullDiscountActivity]: () => outsideComein.fullDiscountActivity(params, extra),
    [OutsideComein.selectiveGiftActivity]: () => outsideComein.selectiveGiftActivity(params, extra),
    [OutsideComein.searchResult]: () => outsideComein.searchResult(params),
    [OutsideComein.search]: () => outsideComein.search(),
    [OutsideComein.blindBoxActivity]: () => outsideComein.blindBoxActivity(params),
    [OutsideComein.wishBoxActivity]: () => outsideComein.wishBoxActivity(params),
    [OutsideComein.PointsMall]: () => outsideComein.pointsMall(),
    [OutsideComein.forum]: () => outsideComein.forum(),
    [OutsideComein.forumCategory]: () => outsideComein.forumCategory(params),
    [OutsideComein.forumTag]: () => outsideComein.forumTag(params),
    [OutsideComein.forumNote]: () => outsideComein.forumNote(params),
    [OutsideComein.shoppingCreditsDeduction]: () =>
      outsideComein.shoppingCreditsDeduction(params, extra),
    [OutsideComein.shoppingCreditsClaim]: () => outsideComein.shoppingCreditsClaim(params, extra),
    [OutsideComein.lotteryActivity]: () => outsideComein.lotteryActivity(params, extra),
    [OutsideComein.none]: () => {},
  };

  actions[type as OutsideComein]?.();
};

export default {
  // page-show 处理小程序、短链接等
  show: async (options: App.LaunchShowOption | undefined) => {
    if (options) {
      // 二维码、短链接
      const sceneStr = options.query?.scene || null;
      // 好友转发
      const friend = options.query?.friend || null;

      let shareDistributorData: MpApp.shareDistributor | null = null;

      if (sceneStr) {
        console.log(`<二维码、短链接场景>`);
        // 解析场景值
        shareDistributorData = await fetchDesCode(sceneStr);
      } else if (friend) {
        console.log(`<好友转发场景>`);
        shareDistributorData = options.query as MpApp.shareDistributor;
      }

      console.log(`<App onShow 分享分销数据>`, shareDistributorData);

      if (shareDistributorData) {
        // 缓存分享分销数据
        useAppStore().shareDistributorData = shareDistributorData;

        // options存在路径时，不执行类型对应的操作。
        if (`/${options.path}` === PAGE_PATH.home.route) {
          await executeOnShowActions(
            shareDistributorData.shareType,
            shareDistributorData.shareValue,
            !!shareDistributorData.isDetail
          );
        }

        // 兼容onShow已登录时，执行绑定
        if (useUserStore().isLogin && useUserStore().userInfo) {
          outsideComein.distribution(shareDistributorData.distributorId);
        } else {
          if (shareDistributorData.distributorId) {
            // 用户未登录的情况下，需缓存用户的分享分销信息
            storage.set(NEW_USER_TEMP_SHARE_DISTRIBUTOR, shareDistributorData);
            console.log(
              `分享分销 => 用户未登录，缓存已设置：`,
              storage.get(NEW_USER_TEMP_SHARE_DISTRIBUTOR)
            );
          }
        }
      }
    }
  },
  /**
   * 根据类型跳转目标场景
   * @param arg - 携带参数，目标值
   * @param type - 跳转类型
   */
  customJump: (arg: any, type: UnionKey.CustomJumpType) => {
    const config: Record<CustomJumpType, () => void> = {
      [CustomJumpType.url]: () => {
        uni.navigateTo({ url: `${PAGE_PATH.web.route}?src=${arg}` });
      },
      [CustomJumpType.productDetails]: () => outsideComein.product(arg),
      [CustomJumpType.activePage]: () => outsideComein.customPage(arg),
      [CustomJumpType.coupon]: () => outsideComein.coupon(arg),
      [CustomJumpType.couponBundle]: () => outsideComein.couponBundle(arg),
      [CustomJumpType.fullDiscount]: () => outsideComein.fullDiscountActivity(arg, 'fetchDetail'),
      [CustomJumpType.discountByQuantity]: () => outsideComein.discountActivity(arg, 'fetchDetail'),
      [CustomJumpType.purchasePremium]: () => outsideComein.purchasePremium(arg, 'fetchDetail'),
      [CustomJumpType.packageDiscountActivity]: () =>
        outsideComein.packageDiscount(arg, 'fetchDetail'),
      [CustomJumpType.memberShipPlan]: () => outsideComein.memberShipPlan(arg),
      [CustomJumpType.membershipPlanListPage]: () => outsideComein.memberShipPlan(''),
      [CustomJumpType.checkinPage]: () => outsideComein.checkinPage(arg),
      [CustomJumpType.smallProgram]: () => outsideComein.miniProgram(arg),
      [CustomJumpType.blindBoxActivity]: () => outsideComein.blindBoxActivity(arg),
      [CustomJumpType.wishBoxActivity]: () => outsideComein.wishBoxActivity(arg),
      [CustomJumpType.PointsMall]: () => outsideComein.pointsMall(),
      [CustomJumpType.forum]: () => outsideComein.forum(),
      [CustomJumpType.forumCategory]: () => outsideComein.forumCategory(arg),
      [CustomJumpType.forumTag]: () => outsideComein.forumTag(arg),
      [CustomJumpType.shoppingCreditsDeduction]: () =>
        outsideComein.shoppingCreditsDeduction(arg, 'fetchDetail'),
      [CustomJumpType.shoppingCreditsClaim]: () =>
        outsideComein.shoppingCreditsClaim(arg, 'fetchDetail'),
      [CustomJumpType.blank]: () => {},
      [CustomJumpType.popupPurchase]: () => {},
      [CustomJumpType.lotteryActivity]: () => outsideComein.lotteryActivity(arg),
    };

    config[type]?.();
  },
  /**
   * 处理分享分销输出
   * @param share
   * @param options
   * @returns
   */
  shareDistributorOutput: (
    share: Page.ShareAppMessageOption,
    options: Page.CustomShareContent & { shareType?: string; shareValue?: string }
  ) => {
    // 判断path是否已包含问号，决定使用?还是&作为连接符
    const connector = options.path?.includes('?') ? '&' : '?';
    let path = `${options.path}${connector}friend=Y&shareType=${options.shareType}&shareValue=${
      options.shareValue
    }&isDetail=${false}&path=`;

    const distributorId = useAppStore().distributorData?.id || '';

    // 来自页面内分享赚按钮
    if (distributorId) {
      path = `${path}&distributorId=${distributorId}`;
    } else {
      console.log(`<分享分销员ID不存在，请检查>`, useAppStore().distributorData);
    }

    options = {
      ...options,
      path,
    };

    console.log(`<转发好友分享>`, options);

    return options;
  },
  /** 获取解析后的分享分销数据 */
  getShareDistributorData: async (options: AnyObject | undefined) => {
    let data;
    // scene参数存在，则是从分享分销跳转而来
    if (options?.scene) {
      data = await businessUtils.outsideComein.parseOptionsQueryScene(options.scene);
    } else if (options?.friend) {
      data = options;
    }
    return data;
  },
  /**
   * 客服
   * @param e
   */
  customerService: (e: any) => {
    uni.redirectTo({ url: `${e.detail.path}?id=${e.detail.query?.id}&crm=${sceneConfig.channel}` });
  },
  mockUser: (mockUserToken: string) => {
    if (mockUserToken) {
      storage.set(TOKEN, {
        value: mockUserToken,
        setTime: new Date().getTime(),
        expiryTime: 86400000,
      });
      console.log(`<已开启用户模拟>`, mockUserToken);
    }
  },
  /**
   * 获取场景值信息并上报
   * @param options
   */
  scene: (options?: any) => {
    let scene = options?.scene;
    
    const cachedScene = storage.get(WECHAT_SCENE_KEY);
    if (cachedScene) {
      scene = cachedScene;
    }

    console.log(`<scene>`, scene);

    if (scene) {
      if (useUserStore().isLogin) {
        if (cachedScene) storage.remove(WECHAT_SCENE_KEY);

        const sceneInfo = wechatMpSceneConfig[scene];
        console.log(`<获取场景值信息并上报>`, sceneInfo);

        let sourceType = '';
        // #ifdef MP-WEIXIN
        sourceType = 'wechat';
        // #endif
        // #ifdef H5
        sourceType = 'h5';
        // #endif

        if (sceneInfo) {
          fetchAddCustomerSourceMiniProgram({
            sourceType,
            sourceValue: sceneInfo.id.toString(),
          });
        }
      } else {
        console.log(`<获取场景值信息并上报当没有登录，暂存到storage>`, scene);
        storage.set(WECHAT_SCENE_KEY, scene);
      }
    }
  },
  /**
   * H5端设置微信分享数据
   * 自动拼接 friend、shareType、shareValue、distributorId 等分销参数
   * 公众号菜单进入、扫码进入、收藏里打开、公众号推文中的链接进入才会触发卡片信息显示
   * @param options - 分享配置
   */
  setH5WechatShare: (options: {
    title?: string;
    desc?: string;
    link?: string;
    imgUrl?: string;
    shareType?: string;
    shareValue?: string;
    /** 页面特有的查询参数，会覆盖/追加到链接上 */
    queryParams?: Record<string, string>;
  }) => {
    // #ifdef H5
    // 构建分享链接，自动附加分销参数
    let link = options.link || location.href;
    const url = new URL(link);

    // 移除已有的分销相关参数，避免重复
    ['friend', 'shareType', 'shareValue', 'isDetail', 'path', 'distributorId', 'openIdKey'].forEach((key) => {
      url.searchParams.delete(key);
    });

    // 设置页面特有的查询参数（覆盖/追加）
    if (options.queryParams) {
      Object.entries(options.queryParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    // 拼接分销参数
    url.searchParams.set('friend', 'Y');
    if (options.shareType) {
      url.searchParams.set('shareType', options.shareType);
    }
    if (options.shareValue) {
      url.searchParams.set('shareValue', options.shareValue);
    }
    url.searchParams.set('isDetail', 'false');
    url.searchParams.set('path', '');

    const distributorId = useAppStore().distributorData?.id || '';
    if (distributorId) {
      url.searchParams.set('distributorId', distributorId);
    }

    link = url.toString();

    WechatSDK.ready(() => {
      WechatSDK.updateAppMessageShareData({
        title: options.title || '',
        desc: options.desc || '',
        link,
        imgUrl: options.imgUrl || '',
        success: () => {
          console.log('[H5分享转发好友] 设置成功', { ...options, link });
        },
      });

      WechatSDK.updateTimelineShareData({
        title: options.title || '',
        link,
        imgUrl: options.imgUrl || '',
        success: () => {
          console.log('[H5分享朋友圈] 设置成功', { ...options, link });
        },
      });

      WechatSDK.onMenuShareAppMessage({
        title: options.title || '',
        desc: options.desc || '',
        link,
        imgUrl: options.imgUrl || '',
        success: () => {
          console.log('[H5分享转发好友] 设置成功', { ...options, link });
        },
        cancel: () => {
          console.log('[H5分享转发好友] 用户取消分享', { ...options, link });
        }
      });

      WechatSDK.onMenuShareTimeline({
        title: options.title || '',
        link,
        imgUrl: options.imgUrl || '',
        success: () => {
          console.log('[H5分享朋友圈] 设置成功', { ...options, link });
        },
        cancel: () => {
          console.log('[H5分享朋友圈] 用户取消分享', { ...options, link });
        }
      });

    });
    // #endif
  },
};
